import { useEffect, useRef } from "react";

const DEFAULT_INTERVAL_MS = 5000;
// Backoff ceiling for a thread nobody is talking on, so an idle tab stops
// hammering the API at the base interval forever.
const MAX_INTERVAL_MS = 30000;

type Options = {
  /** Pauses polling entirely (e.g. no thread selected, or the thread is closed). */
  enabled?: boolean;
  intervalMs?: number;
  /**
   * A value that changes whenever the thread actually changed — typically the
   * message count. When it stops changing the interval backs off; when it
   * changes the interval resets to the base rate.
   */
  activitySignal?: unknown;
  /**
   * Idle threads back off toward MAX_INTERVAL_MS. Inbox lists should pass
   * false so new conversations keep arriving on a steady cadence.
   */
  backoff?: boolean;
};

/**
 * Polls while the tab is visible, with backoff on an idle thread.
 *
 * The thread views previously used a bare `setInterval(fetch, 3000)`, which kept
 * refetching the whole conversation for every background tab and every idle
 * thread. Each of those requests re-read the full message list server-side.
 *
 * Replace this with a websocket or SSE when there's a channel for it; the call
 * sites won't need to change.
 */
export function useThreadPolling(poll: () => void, options: Options = {}) {
  const {
    enabled = true,
    intervalMs = DEFAULT_INTERVAL_MS,
    activitySignal,
    backoff = true,
  } = options;

  // Held in a ref so a new inline callback on each render doesn't restart the timer.
  const pollRef = useRef(poll);
  pollRef.current = poll;

  const delayRef = useRef(intervalMs);
  const lastSignalRef = useRef(activitySignal);

  useEffect(() => {
    if (activitySignal !== lastSignalRef.current) {
      lastSignalRef.current = activitySignal;
      delayRef.current = intervalMs;
    }
  }, [activitySignal, intervalMs]);

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(run, delayRef.current);
    };

    const run = () => {
      if (document.visibilityState === "visible") {
        pollRef.current();
        delayRef.current = backoff
          ? Math.min(delayRef.current * 1.5, MAX_INTERVAL_MS)
          : intervalMs;
      }
      schedule();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Catch up immediately on refocus, then resume at the base rate.
      delayRef.current = intervalMs;
      pollRef.current();
      if (timer) clearTimeout(timer);
      schedule();
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, intervalMs, backoff]);
}

export default useThreadPolling;
